import { Station } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { LAYOUT_MODEL_SYNCHRONIZED_ACTIONS } from '@paragon/layout-models';
import { getLoggedInUserOrganization } from '@paragon/login-tools';
import { enqueueError } from '@paragon/notification-tools';
import { makeIdSafe } from '@paragon/sync-client';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { syncClient } from '../store';
import { setDistanceFormat } from '../store/applicationSlice';
import { linesLoaded } from '../store/linesSlice';
import { productionGroupsLoaded } from '../store/productionGroupsSlice';
import { scheduleGroupsLoaded } from '../store/scheduleGroupsSlice';
import { scheduleLineGroupsLoaded } from '../store/scheduleLineGroupsSlice';
import { loadStation, stationsLoaded } from '../store/stationsSlice';
import { loadProductionGroupsData } from './loadProductionGroupsData';
import { loadProjects } from './loadProjects';
import { loadProductionOutputPresets } from './productionOutputPresets';
import { AsyncThunkActionVoid } from './thunkActions';
import { loadTrailers } from './trailerActions';
import { loadTruckDrivers } from './truckDriverActions';
import { loadTruckloads } from './truckloadActions';

export function startApp(): AsyncThunkActionVoid {
  return async function startAppThunkAction(dispatch, getState) {
    const spinnerId = dispatch(addSpinnerMessage('Loading...'));

    try {
      const loadUserSettingsPromise = designService.userSettings
        .getUserSettings()
        .then((userSettings) => void dispatch(setDistanceFormat(userSettings.viewOptionSettings.distanceFormat)))
        .catch((error) => {
          // The user may not have settings, which comes back as a 404
          if (error.status !== 404) {
            console.error(error);
          }
        });

      const loadProjectsPromise = dispatch(loadProjects());

      const loadProductionGroupsPromise = designService.productionGroups
        .getActiveForOrganization()
        .then((productionGroups) => void dispatch(productionGroupsLoaded(productionGroups)));
      const loadScheduleGroupsPromise = designService.scheduleGroups
        .getForOrganization()
        .then((scheduleGroups) => void dispatch(scheduleGroupsLoaded(scheduleGroups)));
      const loadScheduleLineGroupsPromise = designService.scheduleLineGroups
        .getForOrganization()
        .then((scheduleLineGroups) => void dispatch(scheduleLineGroupsLoaded(scheduleLineGroups)));

      const loadProductionOutputPresetsPromise = dispatch(loadProductionOutputPresets());
      const loadTruckloadsPromise = dispatch(loadTruckloads(false));
      const loadTrailersPromise = dispatch(loadTrailers());
      const loadTruckDriversPromise = dispatch(loadTruckDrivers());

      const loadLinesPromise = designService.lines.get().then(({ lines }) => dispatch(linesLoaded(lines)));
      const loadStationsPromise = designService.stations.getStationsForOrganizationShallow().then((stations) => {
        dispatch(stationsLoaded(stations));
        return stations;
      });

      await loadProductionGroupsPromise;
      const activeProductionGroups = Object.values(getState().productionGroups);
      await dispatch(loadProductionGroupsData(activeProductionGroups.map(({ guid }) => guid)));

      const stations = await loadStationsPromise;
      setupSynchronization(stations, getLoggedInUserOrganization(getState())!);

      await Promise.all([
        loadUserSettingsPromise,
        loadScheduleGroupsPromise,
        loadScheduleLineGroupsPromise,
        loadProductionOutputPresetsPromise,
        loadTruckloadsPromise,
        loadTrailersPromise,
        loadTruckDriversPromise,
        loadLinesPromise,
        loadStationsPromise,
        loadProjectsPromise,
      ]);
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Error loading data'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

function setupSynchronization(stations: readonly Station[], organization: string) {
  const stationConfigurations = stations.map((station) => ({
    room: `#station-${station.guid}`,
    synchronizedActions: [loadStation.type],
  }));
  const projectConfiguration = {
    room: `#projects-${makeIdSafe(organization)}`,
    synchronizedActions: LAYOUT_MODEL_SYNCHRONIZED_ACTIONS,
  };

  syncClient.state.configurations = [...stationConfigurations, projectConfiguration];
}
